import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  saveProjectToFirebase,
  toggleProjectLike as fbToggleProjectLike,
  toggleProjectFollow as fbToggleProjectFollow,
  incrementProjectShare as fbIncrementProjectShare,
  incrementProjectUpdates as fbIncrementProjectUpdates,
  // optional updates counter; safe to use when we add editing later
  // if it doesn't exist it will be ignored by callers
} from '../../pages/home/Projects/api/projectOperations.js';

// Thunks
export const createProject = createAsyncThunk(
  'projects/createProject',
  async (project, { rejectWithValue }) => {
    try {
      const saved = await saveProjectToFirebase(project);
      return saved;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create project');
    }
  }
);

// Save project draft
export const saveDraft = createAsyncThunk(
  'projects/saveDraft',
  async ({ draftData, existingDraftId }, { rejectWithValue }) => {
    try {
      const { saveDraftToFirebase } = await import('../../pages/home/Projects/api/projectOperations.js');
      const saved = await saveDraftToFirebase(draftData, existingDraftId);
      return saved;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to save draft');
    }
  }
);

// Toggle like on a project
export const toggleProjectLike = createAsyncThunk(
  'projects/toggleLike',
  async ({ projectId, userId }, { rejectWithValue }) => {
    try {
      const res = await fbToggleProjectLike(projectId, userId);
      return { projectId, ...res };
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to toggle like');
    }
  }
);

// Toggle follow on a project
export const toggleProjectFollow = createAsyncThunk(
  'projects/toggleFollow',
  async ({ projectId, userId }, { rejectWithValue }) => {
    try {
      const res = await fbToggleProjectFollow(projectId, userId);
      return { projectId, ...res };
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to toggle follow');
    }
  }
);

// Increment share count
export const incrementProjectShare = createAsyncThunk(
  'projects/incrementShare',
  async ({ projectId }, { rejectWithValue }) => {
    try {
      const res = await fbIncrementProjectShare(projectId);
      return { projectId, ...res };
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to share project');
    }
  }
);

// Increment updates count
export const incrementProjectUpdates = createAsyncThunk(
  'projects/incrementUpdates',
  async ({ projectId }, { rejectWithValue }) => {
    try {
      const res = await fbIncrementProjectUpdates(projectId);
      return { projectId, ...res };
    } catch (e) {
      return rejectWithValue(e.message || 'Failed to record update');
    }
  }
);

const initialState = {
  projects: [],
  loading: false,
  error: null,
};

const projectSlice = createSlice({
  name: 'projects',
  initialState,
  reducers: {
    clearProjectError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createProject.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.projects = [action.payload, ...state.projects];
      })
      .addCase(createProject.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Save Draft
      .addCase(saveDraft.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveDraft.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Add or update draft in projects list
        const existingIndex = state.projects.findIndex(p => p.id === action.payload.id);
        if (existingIndex >= 0) {
          state.projects[existingIndex] = action.payload;
        } else {
          state.projects = [action.payload, ...state.projects];
        }
      })
      .addCase(saveDraft.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Social counters
      .addCase(toggleProjectLike.fulfilled, (state, action) => {
        const { projectId, likes } = action.payload;
        const p = state.projects.find(p => p.id === projectId);
        if (p) p.likeCount = likes;
      })
      .addCase(toggleProjectFollow.fulfilled, (state, action) => {
        const { projectId, followers } = action.payload;
        const p = state.projects.find(p => p.id === projectId);
        if (p) p.followerCount = followers;
      })
      .addCase(incrementProjectShare.fulfilled, (state, action) => {
        const { projectId, shares } = action.payload;
        const p = state.projects.find(p => p.id === projectId);
        if (p) p.shareCount = shares;
      })
      .addCase(incrementProjectUpdates.fulfilled, (state, action) => {
        const { projectId, updates } = action.payload;
        const p = state.projects.find(p => p.id === projectId);
        if (p) p.updatesCount = updates;
      });
  }
});

export const { clearProjectError } = projectSlice.actions;
export default projectSlice.reducer;
